import React from 'react';
import { PolicySetting } from '../../../../types/types';
import i18n from '@/content';
import {
    CONFLICT_RULES,
    ConflictRule,
    getSettingDisplayName,
    getMutualExclusionConflicts,
    getOneToManyConflicts,
    getRequiredAtLeastOneRules,
} from './conflictConfig';
import { SQUASH_SETTINGS } from '../editorTransformation/config';
import styles from '../../../../components/AdminPanel/PolicyEditor/ConflictModal/ConflictModal.module.scss';

/**
 * Information about a detected conflict
 */
export interface ConflictInfo {
    rule: ConflictRule;
    targetSettingId: string;
    conflictingSettingIds: string[];
    /** Can be a React element with formatting */
    message: React.ReactNode;
}

/**
 * Result of validating required settings (used at save time)
 */
export interface ValidationResult {
    isValid: boolean;
    errorMessage?: string;
    errorMessageKey?: string;
}

/**
 * Create a settings map from an array of PolicySetting objects
 */
const createSettingsMap = (
    settings: PolicySetting[],
): Record<string, PolicySetting> => settings.reduce<Record<string, PolicySetting>>(
    (acc, setting) => ({ ...acc, [setting.id]: setting }),
    {},
);

/**
 * Check if a boolean setting is effectively enabled, considering dropdown representations
 * This handles cases where boolean settings are "squashed" into dropdown settings
 */
const isSettingEffectivelyEnabled = (settingId: string, settingsMap: Record<string, PolicySetting>): boolean => {
    // First check if the setting exists directly as a boolean
    const directSetting = settingsMap[settingId];
    if (directSetting && typeof directSetting.value === 'boolean') {
        return directSetting.value;
    }

    // Check if this boolean setting is represented by a dropdown (generic approach)
    for (const [dropdownId, squashConfig] of Object.entries(SQUASH_SETTINGS)) {
        // Check if this setting is one of the "from" booleans
        const isBooleanFromDropdown = squashConfig.from.some((template) => template.id === settingId);

        if (isBooleanFromDropdown) {
            const dropdown = settingsMap[dropdownId];
            if (dropdown && typeof dropdown.value === 'string') {
                const dropdownValue = dropdown.value;
                const mapping = squashConfig.rules[dropdownValue];

                if (mapping) {
                    // Handle array of possible boolean combinations
                    if (Array.isArray(mapping)) {
                        return mapping.some((combo) => combo[settingId] === true);
                    }
                    // Handle single boolean combination
                    return mapping[settingId] === true;
                }
            }
        }
    }

    return false;
};

/**
 * Check if a setting is currently enabled (legacy function for backward compatibility)
 */
const isSettingEnabled = (
    settingId: string,
    settingsMap: Record<string, PolicySetting>,
): boolean => isSettingEffectivelyEnabled(settingId, settingsMap);

/**
 * Get the UI-visible setting ID for a given setting
 * If a boolean setting is represented by a dropdown, returns the dropdown ID with the specific value
 */
const getVisibleSettingId = (settingId: string, settingsMap: Record<string, PolicySetting>): string => {
    // If the setting exists directly in the map, return it as-is
    if (settingsMap[settingId]) {
        return settingId;
    }

    // Check if this boolean setting is represented by a dropdown (generic approach)
    for (const [dropdownId, squashConfig] of Object.entries(SQUASH_SETTINGS)) {
        const isBooleanFromDropdown = squashConfig.from.some((template) => template.id === settingId);

        if (isBooleanFromDropdown) {
            const dropdown = settingsMap[dropdownId];
            if (dropdown && typeof dropdown.value === 'string') {
                // Return dropdown ID with the specific value for better messaging
                return `${dropdownId}.${dropdown.value}`;
            }
        }
    }

    // Default: return the original setting ID
    return settingId;
};

/**
 * Generate a localized user-friendly conflict message using i18n
 * Returns JSX with bolded feature names for better readability
 *
 * @param targetSettingId - The setting being enabled/changed
 * @param conflictingSettingIds - Array of conflicting setting IDs
 * @param messageKey - i18n key for the message template (e.g., 'conflictSingle', 'conflictMultiple')
 * @returns Localized message with interpolated and styled setting names
 */
export const formatConflictMessage = (
    targetSettingId: string,
    conflictingSettingIds: string[],
    messageKey: string,
): React.ReactNode => {
    const nameSpace = 'policyEditor.messages.incompatibleSettingsWarnings';
    const targetName = getSettingDisplayName(targetSettingId);

    // Dynamically choose the right message key based on actual conflict count
    // This ensures we use the template that matches the data we're providing
    let actualMessageKey = messageKey;
    if (messageKey === 'conflictSingle' || messageKey === 'conflictMultiple') {
        actualMessageKey = conflictingSettingIds.length === 1 ? 'conflictSingle' : 'conflictMultiple';
    }

    // Constants for placeholder strings
    const PLACEHOLDER_A = '<<<SETTING_A>>>';
    const PLACEHOLDER_B = '<<<SETTING_B>>>';
    const PLACEHOLDER_LIST = '<<<SETTINGS_LIST>>>';

    // Helper to wrap setting names in semi-bold style for emphasis
    const boldName = (name: string, key: string): React.ReactNode => (
        <span key={key} className={styles['setting-name']}>{name}</span>
    );

    if (conflictingSettingIds.length === 1) {
        // Single conflict - parse and replace all occurrences
        const conflictingSettingName = getSettingDisplayName(conflictingSettingIds[0]);

        // Get the template with unique placeholders
        const template = i18n.t(`${nameSpace}.${actualMessageKey}`, {
            settingA: PLACEHOLDER_A,
            settingB: PLACEHOLDER_B,
        });

        // Parse the template and build JSX with bolded names
        const parts: React.ReactNode[] = [];
        let remainingText = template;
        let keyCounter = 0;

        while (remainingText.length > 0) {
            const settingAIndex = remainingText.indexOf(PLACEHOLDER_A);
            const settingBIndex = remainingText.indexOf(PLACEHOLDER_B);

            // Find which placeholder comes first
            if (settingAIndex === -1 && settingBIndex === -1) {
                // No more placeholders
                parts.push(remainingText);
                break;
            }

            if (settingAIndex !== -1 && (settingBIndex === -1 || settingAIndex < settingBIndex)) {
                // Setting A comes first
                parts.push(remainingText.substring(0, settingAIndex));
                parts.push(boldName(targetName, `setting-a-${keyCounter}`));
                remainingText = remainingText.substring(settingAIndex + PLACEHOLDER_A.length);
                keyCounter += 1;
            } else {
                // Setting B comes first
                parts.push(remainingText.substring(0, settingBIndex));
                parts.push(boldName(conflictingSettingName, `setting-b-${keyCounter}`));
                remainingText = remainingText.substring(settingBIndex + PLACEHOLDER_B.length);
                keyCounter += 1;
            }
        }

        // Return single element directly, or array for React to render
        if (parts.length === 1) {
            return parts[0];
        }
        return parts;
    }

    // Multiple conflicts - parse and replace all occurrences
    const conflictingNames = conflictingSettingIds.map(getSettingDisplayName);

    const template = i18n.t(`${nameSpace}.${actualMessageKey}`, {
        settingA: PLACEHOLDER_A,
        settingsList: PLACEHOLDER_LIST,
    });

    // Build the formatted list for multiple settings
    const formattedListParts: React.ReactNode[] = [];
    conflictingNames.forEach((name, index) => {
        if (index > 0) {
            formattedListParts.push(index === conflictingNames.length - 1 ? ' and ' : ', ');
        }
        formattedListParts.push(
            boldName(name, `conflict-setting-${conflictingSettingIds[index]}`),
        );
    });

    // Parse the template
    const parts: React.ReactNode[] = [];
    let remainingText = template;
    let keyCounter = 0;

    while (remainingText.length > 0) {
        const settingAIndex = remainingText.indexOf(PLACEHOLDER_A);
        const settingsListIndex = remainingText.indexOf(PLACEHOLDER_LIST);

        if (settingAIndex === -1 && settingsListIndex === -1) {
            parts.push(remainingText);
            break;
        }

        if (settingAIndex !== -1 && (settingsListIndex === -1 || settingAIndex < settingsListIndex)) {
            parts.push(remainingText.substring(0, settingAIndex));
            parts.push(boldName(targetName, `setting-a-${keyCounter}`));
            remainingText = remainingText.substring(settingAIndex + PLACEHOLDER_A.length);
            keyCounter += 1;
        } else {
            parts.push(remainingText.substring(0, settingsListIndex));
            parts.push(<React.Fragment key={`list-${keyCounter}`}>{formattedListParts}</React.Fragment>);
            remainingText = remainingText.substring(settingsListIndex + PLACEHOLDER_LIST.length);
            keyCounter += 1;
        }
    }

    // Return single element directly, or array for React to render
    if (parts.length === 1) {
        return parts[0];
    }
    return parts;
};

/**
 * Detect mutual exclusion conflicts when enabling a setting
 * Collects ALL conflicting settings from ALL mutual exclusion rules
 */
const detectMutualExclusionConflict = (
    settingId: string,
    newValue: boolean,
    settingsMap: Record<string, PolicySetting>,
): ConflictInfo | null => {
    // Only check when enabling a setting
    if (!newValue) {
        return null;
    }

    const conflicts = getMutualExclusionConflicts(settingId);

    // Collect ALL conflicting settings from ALL rules (not just the first one)
    const allConflictingSettings: string[] = [];
    let firstRule: ConflictRule | null = null;

    for (const rule of conflicts) {
        const otherSettingIds = rule.settings.filter((id) => id !== settingId);
        const conflictingEnabled = otherSettingIds.filter((id) => isSettingEnabled(id, settingsMap));

        if (conflictingEnabled.length > 0) {
            // Remember the first rule for metadata purposes
            if (!firstRule) {
                firstRule = rule;
            }
            // Map to UI-visible setting IDs (handles dropdowns that represent boolean settings)
            const visibleSettingIds = conflictingEnabled.map((id) => getVisibleSettingId(id, settingsMap));
            allConflictingSettings.push(...visibleSettingIds);
        }
    }

    // Remove duplicates in case multiple rules mention the same setting
    const uniqueConflictingSettings = [...new Set(allConflictingSettings)];

    if (uniqueConflictingSettings.length > 0 && firstRule) {
        return {
            rule: firstRule,
            targetSettingId: settingId,
            conflictingSettingIds: uniqueConflictingSettings,
            message: formatConflictMessage(
                settingId,
                uniqueConflictingSettings,
                firstRule.messageKey || '',
            ),
        };
    }

    return null;
};

/**
 * Detect one-to-many conflicts when enabling a setting
 */
const detectOneToManyConflict = (
    settingId: string,
    newValue: boolean,
    settingsMap: Record<string, PolicySetting>
): ConflictInfo | null => {
    const conflicts = getOneToManyConflicts(settingId);

    for (const rule of conflicts) {
        // Check if we're enabling the trigger setting
        if (rule.triggerSetting === settingId && newValue === rule.triggerValue) {
            // Find which of the affected settings are currently enabled
            const affectedSettings = rule.settings.filter((id) => id !== settingId);
            const conflictingEnabled = affectedSettings.filter((id) => isSettingEnabled(id, settingsMap));

            if (conflictingEnabled.length > 0) {
                return {
                    rule,
                    targetSettingId: settingId,
                    conflictingSettingIds: conflictingEnabled,
                    message: formatConflictMessage(settingId, conflictingEnabled, rule.messageKey || ''),
                };
            }
        }
    }

    return null;
};

/**
 * Check if disabling a setting would violate a "required at least one" rule
 */
const detectRequiredAtLeastOneViolation = (
    settingId: string,
    newValue: boolean,
    settingsMap: Record<string, PolicySetting>
): ConflictInfo | null => {
    // Only check when disabling a setting
    if (newValue) {
        return null;
    }

    const rules = getRequiredAtLeastOneRules(settingId);

    for (const rule of rules) {
        // For conditional rules, check if the trigger is active
        if (rule.triggerSetting) {
            const triggerEnabled = isSettingEnabled(rule.triggerSetting, settingsMap);
            if (triggerEnabled !== rule.triggerValue) {
                continue; // Rule doesn't apply in current state
            }
        }

        // Check how many of the required settings would be enabled after the change
        const enabledCount = rule.settings.filter((id) => {
            if (id === settingId) {
                return false; // This one is being disabled
            }
            return isSettingEnabled(id, settingsMap);
        }).length;

        if (enabledCount === 0) {
            // This would be a violation - can't disable this setting
            return {
                rule,
                targetSettingId: settingId,
                conflictingSettingIds: [],
                message: '', // This type of conflict prevents the action, no "turn off" message
            };
        }
    }

    return null;
};

/**
 * Detect any conflicts when toggling a setting
 * Returns conflict information if a conflict is detected, null otherwise
 */
export const detectConflictOnToggle = (
    settingId: string,
    newValue: boolean,
    currentSettings: PolicySetting[]
): ConflictInfo | null => {
    const settingsMap = createSettingsMap(currentSettings);

    // Check for mutual exclusion conflicts
    const mutualExclusion = detectMutualExclusionConflict(settingId, newValue, settingsMap);
    if (mutualExclusion) {
        return mutualExclusion;
    }

    // Check for one-to-many conflicts
    const oneToMany = detectOneToManyConflict(settingId, newValue, settingsMap);
    if (oneToMany) {
        return oneToMany;
    }

    // Check for "required at least one" violations
    const requiredViolation = detectRequiredAtLeastOneViolation(settingId, newValue, settingsMap);
    if (requiredViolation) {
        return requiredViolation;
    }

    return null;
};

/**
 * Get list of currently enabled settings that conflict with the given setting
 */
export const getConflictingEnabledSettings = (
    settingId: string,
    currentSettings: PolicySetting[]
): string[] => {
    const settingsMap = createSettingsMap(currentSettings);
    const conflicting: string[] = [];

    // Check mutual exclusions
    const mutualExclusions = getMutualExclusionConflicts(settingId);
    for (const rule of mutualExclusions) {
        const otherSettings = rule.settings.filter((id) => id !== settingId);
        const enabled = otherSettings.filter((id) => isSettingEnabled(id, settingsMap));
        conflicting.push(...enabled);
    }

    return [...new Set(conflicting)]; // Remove duplicates
};

/**
 * Get array of setting IDs that need to be disabled to resolve a conflict
 */
export const getSettingsToDisable = (conflictInfo: ConflictInfo): string[] =>
    conflictInfo.conflictingSettingIds;

/**
 * Validate all "required at least one" rules at save time
 * Returns validation result with error message if validation fails
 */
export const validateRequiredSettings = (allSettings: PolicySetting[]): ValidationResult => {
    const settingsMap = createSettingsMap(allSettings);

    const requiredRules = CONFLICT_RULES.filter((rule) => rule.type === 'required_at_least_one');

    for (const rule of requiredRules) {
        // For conditional rules, check if the trigger is active
        if (rule.triggerSetting) {
            const triggerEnabled = isSettingEnabled(rule.triggerSetting, settingsMap);
            if (triggerEnabled !== rule.triggerValue) {
                continue; // Rule doesn't apply in current state
            }
        }

        // Check if at least one of the required settings is enabled
        const enabledCount = rule.settings.filter((id) => isSettingEnabled(id, settingsMap)).length;

        if (enabledCount === 0) {
            return {
                isValid: false,
                errorMessageKey: rule.messageKey,
                errorMessage: rule.messageKey || 'At least one setting must be enabled',
            };
        }
    }

    return {
        isValid: true,
    };
};

/**
 * Check if a setting should be hidden based on visibility rules
 */
export const shouldHideSettingByVisibility = (
    settingId: string,
    currentSettings: PolicySetting[]
): boolean => {
    const settingsMap = createSettingsMap(currentSettings);

    const visibilityRules = CONFLICT_RULES.filter(
        (rule) => rule.type === 'visibility' && rule.settings.includes(settingId)
    );

    for (const rule of visibilityRules) {
        if (rule.triggerSetting && rule.triggerValue !== undefined) {
            const triggerEnabled = isSettingEnabled(rule.triggerSetting, settingsMap);
            if (triggerEnabled === rule.triggerValue) {
                return true; // Hide this setting
            }
        }
    }

    return false;
};

/**
 * Detect conflicts when changing a dropdown that represents multiple boolean settings
 * This is a generic helper for components like PolicyRadioRow
 * 
 * @param dropdownId - ID of the dropdown setting
 * @param newValue - New dropdown value being selected
 * @param currentSettings - Current policy settings array
 * @returns ConflictInfo if a conflict is detected, null otherwise
 */
export const detectConflictOnDropdownChange = (
    dropdownId: string,
    newValue: string,
    currentSettings: PolicySetting[],
): ConflictInfo | null => {
    const squashConfig = SQUASH_SETTINGS[dropdownId];
    if (!squashConfig) {
        return null; // Not a squashed dropdown
    }

    const mapping = squashConfig.rules[newValue];
    if (!mapping) {
        return null; // No mapping for this value
    }

    // Get the boolean settings that would be enabled by this selection
    const mappingsToCheck = Array.isArray(mapping) ? mapping : [mapping];

    // Check each boolean setting that would be enabled for conflicts
    for (const booleanMapping of mappingsToCheck) {
        for (const [booleanSettingId, booleanValue] of Object.entries(booleanMapping)) {
            if (booleanValue === true) {
                const conflict = detectConflictOnToggle(booleanSettingId, true, currentSettings);
                if (conflict) {
                    return conflict;
                }
            }
        }
    }

    return null;
};
